import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import Logo from '../components/Logo';
import ImageInput from '../components/ImageInput';
import { TextField, TextArea, SelectField, Check } from '../components/AdminFields';
import { OrdersAdmin, LocationsAdmin } from '../components/AdminShop';
import RentalsAdmin from '../components/AdminRentals';
import ServiceRequestsAdmin from '../components/AdminService';
import GuidesAdmin from '../components/AdminGuides';
import FinderLeadsAdmin from '../components/AdminFinderLeads';
import TrainingRequestsAdmin from '../components/AdminTraining';
import SiteTextsAdmin from '../components/AdminSiteTexts';
import PagesAdmin from '../components/AdminPages';
import InboxAdmin from '../components/AdminInbox';
import { api } from '../lib/api';
import { LogOut, Plus, Pencil, Trash2, X, Save, ExternalLink, Star, Mail, Upload, Loader2, TrendingUp, TrendingDown, Inbox, Wrench, Briefcase, Activity } from 'lucide-react';
import { ArrowUp, ArrowDown } from 'lucide-react';

const TABS = [
    { key: 'inbox', label: 'Inbox' },
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'machines', label: 'Machines' },
    { key: 'orders', label: 'Orders' },
    { key: 'rentals', label: 'Rentals' },
    { key: 'training', label: 'Training' },
    { key: 'service-requests', label: 'Service Requests' },
    { key: 'pages', label: 'Pages' },
    { key: 'guides', label: 'Guides' },
    { key: 'finder-leads', label: 'Finder Leads' },
    { key: 'locations', label: 'Map' },
    { key: 'services', label: 'Services' },
    { key: 'projects', label: 'Projects' },
    { key: 'videos', label: 'Videos' },
    { key: 'news', label: 'News' },
    { key: 'careers', label: 'Careers' },
    { key: 'why-us', label: 'Why Us' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'branding', label: 'Branding' },
    { key: 'site-texts', label: 'Site texts' },
    { key: 'account', label: 'Account' },
];

const AdminDashboard = () => {
    const { admin, loading, logout } = useAuth();
    const [tab, setTab] = useState('inbox');
    const [unread, setUnread] = useState(0);
    const navigate = useNavigate();
    const refreshUnread = () => api.get('/admin/inbox/unread-count').then((r) => setUnread(r.data.count)).catch(() => {});
    useEffect(() => { if (admin) refreshUnread(); }, [admin, tab]);

    if (loading) return <div className="p-10 text-white/50">Loading…</div>;
    if (!admin) return <Navigate to="/admin/login" replace />;

    const doLogout = async () => {
        await logout();
        navigate('/admin/login');
    };

    return (
        <div className="min-h-screen bg-brand-bg" data-testid="admin-dashboard">
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-brand-bg/90 border-b border-white/10">
                <div className="container-page h-16 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <Link to="/"><Logo size="sm" /></Link>
                        <span className="label-caps text-white/40">/ Admin CMS</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/" className="label-caps text-white/60 hover:text-white transition-colors inline-flex items-center gap-1" data-testid="admin-view-site">
                            <ExternalLink className="h-4 w-4" /> View Site
                        </Link>
                        <span className="text-sm text-white/60 hidden md:inline">{admin.email}</span>
                        <button onClick={doLogout} className="inline-flex items-center gap-2 label-caps border border-white/20 hover:bg-white/10 transition-colors px-3 py-2" data-testid="admin-logout">
                            <LogOut className="h-4 w-4" /> Logout
                        </button>
                    </div>
                </div>
                <div className="container-page flex gap-1 overflow-x-auto">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`px-4 py-3 label-caps border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                                tab === t.key ? 'border-brand-primary text-white' : 'border-transparent text-white/50 hover:text-white'
                            }`}
                            data-testid={`admin-tab-${t.key}`}
                        >
                            {t.label}
                            {t.key === 'inbox' && unread > 0 && (
                                <span className="ml-2 inline-block min-w-[1.25rem] px-1.5 py-0.5 text-[10px] leading-none bg-brand-primary text-black font-bold rounded-full text-center" data-testid="admin-inbox-badge">{unread}</span>
                            )}
                        </button>
                    ))}
                </div>
            </header>

            <main className="container-page py-10">
                {tab === 'inbox' && <InboxAdmin onOpenTab={setTab} onChanged={refreshUnread} />}
                {tab === 'machines' && <MachinesAdmin />}
                {tab === 'orders' && <OrdersAdmin />}
                {tab === 'rentals' && <RentalsAdmin />}
                {tab === 'training' && <TrainingRequestsAdmin />}
                {tab === 'site-texts' && <SiteTextsAdmin />}
                {tab === 'service-requests' && <ServiceRequestsAdmin />}
                {tab === 'pages' && <PagesAdmin />}
                {tab === 'guides' && <GuidesAdmin />}
                {tab === 'finder-leads' && <FinderLeadsAdmin />}
                {tab === 'locations' && <LocationsAdmin />}
                {tab === 'dashboard' && <DashboardAdmin />}
                {tab === 'services' && <ServicesAdmin />}
                {tab === 'projects' && <ProjectsAdmin />}
                {tab === 'videos' && <VideosAdmin />}
                {tab === 'news' && <NewsAdmin />}
                {tab === 'careers' && <CareersAdmin />}
                {tab === 'why-us' && <WhyUsAdmin />}
                {tab === 'contacts' && <ContactsAdmin />}
                {tab === 'branding' && <BrandingAdmin />}
                {tab === 'account' && <AccountAdmin />}
            </main>
        </div>
    );
};

// ----- Machines ------------------------------------------------------------

// ---- Marketplace auto-import field (used in the machine edit modal) ------
const MarketplaceImportField = ({ onImport }) => {
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [ok, setOk] = useState(false);

    const doImport = async () => {
        setErr(''); setOk(false);
        if (!url.includes('dryicemarketplace.com/products/')) {
            setErr('Paste a full URL from Dry Ice Marketplace, e.g. https://dryicemarketplace.com/products/<id>');
            return;
        }
        setLoading(true);
        try {
            const r = await api.post('/admin/machines/import-from-marketplace', { url });
            onImport(r.data);
            setOk(true);
            setTimeout(() => setOk(false), 4000);
        } catch (e) {
            const d = e.response?.data?.detail;
            setErr(typeof d === 'string' ? d : 'Import failed. Check the URL and try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="border border-brand-primary/40 bg-brand-primary/5 p-4 space-y-3" data-testid="mf-marketplace-import">
            <div className="flex items-baseline justify-between gap-3">
                <div>
                    <div className="label-caps text-brand-primary text-xs">/ Auto-fill from Marketplace</div>
                    <div className="text-white/80 text-sm mt-1">
                        Paste a link from <span className="font-mono text-brand-primary">dryicemarketplace.com</span> and we&apos;ll pre-fill everything below.
                    </div>
                </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://dryicemarketplace.com/products/…"
                    className="flex-1 bg-brand-surface2 border border-white/10 focus:border-brand-primary transition-colors text-white p-3 outline-none text-sm font-mono"
                    data-testid="mf-marketplace-url"
                    disabled={loading}
                />
                <button
                    type="button"
                    onClick={doImport}
                    disabled={loading || !url.trim()}
                    className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-brand-ink font-bold px-5 py-3 label-caps text-sm transition-colors whitespace-nowrap"
                    data-testid="mf-marketplace-import-btn"
                >
                    {loading ? 'Importing…' : 'Import & Prefill'}
                </button>
            </div>
            {err && <div className="text-red-300 text-xs" data-testid="mf-marketplace-err">{err}</div>}
            {ok && <div className="text-brand-primary text-xs" data-testid="mf-marketplace-ok">✓ Fields prefilled from Marketplace. Review, adjust anything you want, then hit Save.</div>}
            <div className="text-white/40 text-xs">
                Overwrites: Name, description, manufacturer, main image, gallery, video, price, features, specs, marketplace link.
                <br />Preserves: Category (you pick it), custom fields you&apos;ve already changed on this form.
            </div>
        </div>
    );
};


const emptyMachine = {
    name: '', category: 'dry-ice', short_description: '', description: '', price: '',
    contact_for_price: true, image_url: '', gallery: [], video_url: '', external_link: '',
    financing_available: true, featured: false, published: true,
    specs: [], features: [],
    machine_type: 'machine', purchase_mode: 'quote', inventory_count: '', availability: 'auto', sku: '',
};

const ShopFields = ({ form, upd }) => (
    <div className="border border-brand-primary/40 p-4 space-y-4" data-testid="mf-shop-fields">
        <div className="label-caps text-brand-primary">Shop settings</div>
        <div className="grid grid-cols-2 gap-4">
            <SelectField label="Item type" value={form.machine_type || 'machine'} onChange={(v) => upd('machine_type', v)} testid="mf-machine-type"
                options={[['machine', 'Machine'], ['accessory', 'Accessory / Part']]} />
            <SelectField label="How customers buy it" value={form.purchase_mode || 'quote'} onChange={(v) => upd('purchase_mode', v)} testid="mf-purchase-mode"
                options={[['buy_now', 'Buy Now (add to cart)'], ['quote', 'Quote Only']]} />
            <SelectField label="Availability shown" value={form.availability || 'auto'} onChange={(v) => upd('availability', v)} testid="mf-availability"
                options={[['auto', 'Automatic (from stock count)'], ['in_stock', 'In Stock'], ['ships_7_14', 'Ships in 7–14 days'], ['backorder', 'Backorder']]} />
            <TextField label="Stock on hand (blank = not tracked)" value={form.inventory_count ?? ''} onChange={(v) => upd('inventory_count', v)} type="number" testid="mf-inventory" />
            <TextField label="SKU (optional)" value={form.sku || ''} onChange={(v) => upd('sku', v)} testid="mf-sku" />
        </div>
        <div className="border-t border-white/10 pt-4 space-y-3">
            <Check label="Available to rent (shows on Rental page)" checked={!!form.rentable} onChange={(v) => upd('rentable', v)} testid="mf-rentable" />
            {form.rentable && (
                <div className="grid grid-cols-3 gap-4">
                    <TextField label="Daily rate ($)" type="number" value={form.rental_daily ?? ''} onChange={(v) => upd('rental_daily', v)} testid="mf-rental-daily" />
                    <TextField label="Weekly rate ($)" type="number" value={form.rental_weekly ?? ''} onChange={(v) => upd('rental_weekly', v)} testid="mf-rental-weekly" />
                    <TextField label="Deposit ($)" type="number" value={form.rental_deposit ?? ''} onChange={(v) => upd('rental_deposit', v)} testid="mf-rental-deposit" />
                    <div className="col-span-3"><TextArea label="Rental specs (shown on Rental page)" rows={2} value={form.rental_specs || ''} onChange={(v) => upd('rental_specs', v)} testid="mf-rental-specs" /></div>
                </div>
            )}
        </div>
        <div className="text-xs text-white/50">
            Buy Now needs a price. Automatic availability shows &quot;In Stock&quot; while stock is above 0, then &quot;Ships in 7–14 days&quot; — orders are never blocked.
        </div>
    </div>
);

// ---- Dashboard (conversion overview) --------------------------------------
const StatCard = ({ label, value, hint, testid, icon: Icon }) => (
    <div className="border border-white/10 bg-brand-surface p-5" data-testid={testid}>
        <div className="flex items-start justify-between mb-3">
            <span className="label-caps text-white/50 text-xs">{label}</span>
            {Icon ? <Icon className="h-4 w-4 text-brand-primary" strokeWidth={1.75} /> : null}
        </div>
        <div className="font-display font-black text-4xl text-white leading-none">{value}</div>
        {hint ? <div className="mt-2 text-xs text-white/40">{hint}</div> : null}
    </div>
);

const BarList = ({ title, items, empty = 'No data yet', testid, formatLabel }) => {
    const max = Math.max(1, ...items.map((x) => x.count));
    return (
        <div className="border border-white/10 bg-brand-surface p-5" data-testid={testid}>
            <div className="label-caps text-white/60 text-xs mb-4">{title}</div>
            {items.length === 0 && <div className="text-white/40 text-sm py-6">{empty}</div>}
            <div className="space-y-2.5">
                {items.map((it, i) => {
                    const pct = Math.round((it.count / max) * 100);
                    const label = formatLabel ? formatLabel(it.label) : it.label;
                    return (
                        <div key={`${it.label}-${i}`} data-testid={`${testid}-row-${i}`}>
                            <div className="flex items-baseline justify-between text-xs mb-1">
                                <span className="text-white/80 truncate pr-2">{label}</span>
                                <span className="font-mono text-brand-primary flex-shrink-0">{it.count}</span>
                            </div>
                            <div className="h-1.5 bg-white/5 relative overflow-hidden">
                                <div className="absolute inset-y-0 left-0 bg-brand-primary" style={{ width: `${pct}%` }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const Sparkline = ({ data, testid }) => {
    // Simple bar sparkline (no chart lib). Data = [{date, count}, ...] length 14.
    const max = Math.max(1, ...data.map((d) => d.count));
    return (
        <div className="border border-white/10 bg-brand-surface p-5" data-testid={testid}>
            <div className="flex items-baseline justify-between mb-4">
                <span className="label-caps text-white/60 text-xs">Submissions — last 14 days</span>
                <span className="label-caps text-white/40 text-[10px]">Peak: {max}</span>
            </div>
            <div className="flex items-end gap-1 h-24">
                {data.map((d, i) => {
                    const h = Math.max(2, Math.round((d.count / max) * 100));
                    const isToday = i === data.length - 1;
                    return (
                        <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
                            <div
                                className={`w-full ${d.count > 0 ? 'bg-brand-primary' : 'bg-white/10'} ${isToday ? 'ring-1 ring-brand-primary' : ''}`}
                                style={{ height: `${h}%` }}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="mt-2 flex justify-between text-[10px] text-white/40 font-mono">
                <span>{data[0]?.date?.slice(5)}</span>
                <span>{data[data.length - 1]?.date?.slice(5)} (today)</span>
            </div>
        </div>
    );
};

const DashboardAdmin = () => {
    const [data, setData] = useState(null);
    const [err, setErr] = useState('');

    const load = async () => {
        setErr('');
        try {
            const r = await api.get('/admin/analytics/overview');
            setData(r.data);
        } catch (e) {
            setErr('Could not load analytics.');
        }
    };
    useEffect(() => { load(); }, []);

    if (err) return <div className="text-red-400" data-testid="dashboard-error">{err}</div>;
    if (!data) return <div className="text-white/50" data-testid="dashboard-loading">Loading dashboard…</div>;

    const totals = data.totals || {};
    const formatIntent = (k) => ({
        assessment: 'Industrial Assessment',
        equipment_quote: 'Equipment Quote',
        site_visit: 'Site Visit',
        rental: 'Equipment Rental',
        financing: 'Financing',
        specialist: 'Specialist Consultation',
        general: 'General inquiry',
    })[k] || k;

    return (
        <div data-testid="dashboard-admin">
            <div className="mb-6">
                <h2 className="font-display font-black uppercase text-3xl">Conversion Dashboard</h2>
                <p className="text-white/50 text-sm mt-1">
                    Live overview of what visitors are actually contacting you about. Cross-reference these against your Google Ads campaigns to see which channels convert.
                </p>
            </div>

            {/* Totals row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <StatCard
                    label="Last 7 days"
                    value={totals.last_7_days || 0}
                    hint="Fresh leads this week"
                    testid="dashboard-stat-7d"
                    icon={TrendingUp}
                />
                <StatCard
                    label="Last 30 days"
                    value={totals.last_30_days || 0}
                    hint="Rolling monthly volume"
                    testid="dashboard-stat-30d"
                    icon={Activity}
                />
                <StatCard
                    label="Unread"
                    value={totals.unread || 0}
                    hint="Need a reply"
                    testid="dashboard-stat-unread"
                    icon={Inbox}
                />
                <StatCard
                    label="All time"
                    value={totals.all_time || 0}
                    hint="Every submission stored"
                    testid="dashboard-stat-all"
                    icon={Briefcase}
                />
            </div>

            {/* Sparkline */}
            <div className="mb-4">
                <Sparkline data={data.timeline || []} testid="dashboard-sparkline" />
            </div>

            {/* Breakdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <BarList
                    title="Top intents"
                    items={data.top_intents || []}
                    testid="dashboard-intents"
                    empty="No submissions with a detectable intent yet."
                    formatLabel={formatIntent}
                />
                <BarList
                    title="Most-requested machines"
                    items={data.top_machines || []}
                    testid="dashboard-machines"
                    empty="No specific machines requested yet."
                />
                <BarList
                    title="Preferred services"
                    items={data.top_services || []}
                    testid="dashboard-services"
                    empty="Visitors haven't picked a preferred service yet."
                />
                <BarList
                    title="Industries reaching out"
                    items={data.top_industries || []}
                    testid="dashboard-industries"
                    empty="No industry values submitted yet."
                />
            </div>

            <div className="mt-6 border border-white/10 bg-brand-surface p-4 flex items-center justify-between gap-4">
                <div className="text-white/60 text-sm">
                    Numbers refresh from live form submissions. Google Ads conversions are counted separately in your Ads dashboard using the events I fire on every CTA click.
                </div>
                <button
                    onClick={load}
                    className="label-caps text-brand-primary hover:text-white transition-colors text-xs whitespace-nowrap"
                    data-testid="dashboard-refresh"
                >
                    ↻ Refresh
                </button>
            </div>
        </div>
    );
};



const MachinesAdmin = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const { data } = await api.get('/admin/machines');
        setItems(data);
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const save = async (form) => {
        const payload = {
            ...form,
            price: form.contact_for_price ? null : (form.price === '' ? null : parseFloat(form.price)),
            specs: form.specs.filter((s) => s.label && s.value),
            features: (Array.isArray(form.features) ? form.features : String(form.features).split('\n')).map((s) => s.trim()).filter(Boolean),
            gallery: (form.gallery || []).map((g) => (g || '').trim()).filter(Boolean),
            inventory_count: form.inventory_count === '' || form.inventory_count == null ? null : parseInt(form.inventory_count, 10),
            ...Object.fromEntries(['rental_daily', 'rental_weekly', 'rental_deposit'].map((k) => [k, form[k] === '' || form[k] == null ? null : parseFloat(form[k])])),
        };
        if (form.id) {
            await api.put(`/admin/machines/${form.id}`, payload);
        } else {
            await api.post('/admin/machines', payload);
        }
        setEditing(null);
        load();
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this machine?')) return;
        await api.delete(`/admin/machines/${id}`);
        load();
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display font-black uppercase text-3xl">Machines</h2>
                <button
                    onClick={() => setEditing({ ...emptyMachine })}
                    className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps transition-colors"
                    data-testid="admin-machines-new"
                >
                    <Plus className="h-4 w-4" /> New Machine
                </button>
            </div>

            {loading ? (
                <div className="text-white/50 label-caps">Loading…</div>
            ) : (
                <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-machines-list">
                    {items.length === 0 && <div className="p-6 text-white/50">No machines yet.</div>}
                    {items.map((m) => (
                        <div key={m.id} className="p-4 flex items-center gap-4">
                            <div className="w-14 h-14 bg-brand-surface flex-shrink-0 border border-white/10 overflow-hidden">
                                {m.image_url ? <img src={m.image_url} alt="" className="w-full h-full object-cover" /> : null}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-bold text-white truncate flex items-center gap-2">
                                    {m.name} {m.featured && <Star className="h-4 w-4 text-brand-primary" />}
                                </div>
                                <div className="text-sm text-white/50">{m.category} • {m.contact_for_price ? 'Contact for price' : `$${m.price}`} • {m.purchase_mode === 'buy_now' ? 'Buy Now' : 'Quote Only'}{m.inventory_count != null ? ` • Stock ${m.inventory_count}` : ''} • {m.published ? 'Published' : 'Hidden'}</div>
                            </div>
                            <button onClick={() => setEditing({ ...m, price: m.price ?? '', features: (m.features || []).join('\n'), specs: m.specs || [], gallery: m.gallery || [] })} className="p-2 border border-white/10 hover:bg-white/10 transition-colors" data-testid={`admin-machine-edit-${m.id}`}>
                                <Pencil className="h-4 w-4" />
                            </button>
                            <button onClick={() => remove(m.id)} className="p-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors" data-testid={`admin-machine-delete-${m.id}`}>
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <MachineForm
                    initial={editing}
                    onCancel={() => setEditing(null)}
                    onSave={save}
                />
            )}
        </div>
    );
};

const MachineForm = ({ initial, onCancel, onSave }) => {
    const [form, setForm] = useState({ ...initial, features: Array.isArray(initial.features) ? initial.features.join('\n') : initial.features || '' });
    const bulkRef = useRef(null);
    const [bulkUploading, setBulkUploading] = useState(false);
    const [bulkError, setBulkError] = useState('');

    const upd = (k, v) => setForm({ ...form, [k]: v });
    const updSpec = (i, k, v) => {
        const next = [...(form.specs || [])];
        next[i] = { ...next[i], [k]: v };
        setForm({ ...form, specs: next });
    };
    const addSpec = () => setForm({ ...form, specs: [...(form.specs || []), { label: '', value: '' }] });
    const removeSpec = (i) => setForm({ ...form, specs: form.specs.filter((_, idx) => idx !== i) });

    const updGallery = (i, v) => {
        const next = [...(form.gallery || [])];
        next[i] = v;
        setForm({ ...form, gallery: next });
    };
    const addGallery = () => setForm({ ...form, gallery: [...(form.gallery || []), ''] });
    const removeGallery = (i) => setForm({ ...form, gallery: (form.gallery || []).filter((_, idx) => idx !== i) });
    const moveGallery = (i, dir) => {
        const arr = [...(form.gallery || [])];
        const j = i + dir;
        if (j < 0 || j >= arr.length) return;
        [arr[i], arr[j]] = [arr[j], arr[i]];
        setForm({ ...form, gallery: arr });
    };
    const handleBulkFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        setBulkError('');
        setBulkUploading(true);
        try {
            const results = await Promise.all(files.map(async (file) => {
                const fd = new FormData();
                fd.append('file', file);
                try {
                    const { data } = await api.post('/admin/upload', fd);
                    return { url: data.url };
                } catch (err) {
                    const detail = err.response?.data?.detail;
                    return { error: typeof detail === 'string' ? detail : `Failed: ${file.name}` };
                }
            }));
            const urls = results.filter((r) => r.url).map((r) => r.url);
            const errors = results.filter((r) => r.error).map((r) => r.error);
            setForm((prev) => ({ ...prev, gallery: [...(prev.gallery || []), ...urls] }));
            if (errors.length) setBulkError(errors.join(' • '));
        } finally {
            setBulkUploading(false);
            if (bulkRef.current) bulkRef.current.value = '';
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-machine-modal">
            <div className="w-full max-w-3xl bg-brand-surface border border-white/10 flex flex-col max-h-[calc(100vh-2rem)]">
                <div className="flex items-center justify-between p-6 border-b border-white/10 bg-brand-surface flex-shrink-0">
                    <h3 className="font-display font-black uppercase text-2xl">{form.id ? 'Edit' : 'New'} Machine</h3>
                    <button onClick={onCancel} className="text-white/60 hover:text-white" data-testid="admin-machine-modal-close"><X /></button>
                </div>
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                    <MarketplaceImportField
                        onImport={(data) => {
                            // Convert the returned features array → newline string for the textarea
                            const featuresStr = Array.isArray(data.features) ? data.features.join('\n') : (data.features || '');
                            setForm((prev) => ({
                                ...prev,
                                name: data.name || prev.name,
                                slug: data.slug || prev.slug,
                                short_description: data.short_description || prev.short_description,
                                description: data.description || prev.description,
                                manufacturer: data.manufacturer || prev.manufacturer,
                                image_url: data.image_url || prev.image_url,
                                gallery: Array.isArray(data.gallery) && data.gallery.length ? data.gallery : prev.gallery,
                                video_url: data.video_url || prev.video_url,
                                price: data.price ?? prev.price,
                                contact_for_price: typeof data.contact_for_price === 'boolean' ? data.contact_for_price : prev.contact_for_price,
                                financing_available: typeof data.financing_available === 'boolean' ? data.financing_available : prev.financing_available,
                                features: featuresStr,
                                specs: Array.isArray(data.specs) && data.specs.length ? data.specs : prev.specs,
                                external_link: data.external_link || prev.external_link,
                                featured: typeof data.featured === 'boolean' ? data.featured : prev.featured,
                                machine_type: prev.id ? prev.machine_type : (data.machine_type || prev.machine_type),
                                purchase_mode: prev.id ? prev.purchase_mode : (data.purchase_mode || prev.purchase_mode),
                            }));
                        }}
                    />
                    <TextField label="Name *" value={form.name} onChange={(v) => upd('name', v)} testid="mf-name" />
                    <div className="grid grid-cols-2 gap-4">
                        <SelectField label="Category" value={form.category} onChange={(v) => upd('category', v)} testid="mf-category"
                            options={[
                                ['dry-ice', 'Dry Ice Cleaner'],
                                ['detailing-light-industrial', 'Detailing / Light Industrial'],
                                ['sandblasting', 'Sandblasting'],
                                ['pressure-washing', 'Pressure Washing'],
                                ['laser', 'Laser'],
                                ['used', 'Used'],
                                ['accessories', 'Accessories'],
                                ['trailer', 'Trailers'],
                                ['air-compressor', 'Air Compressors'],
                            ]}
                        />
                        <div />
                    </div>
                    <ImageInput label="Main image" value={form.image_url} onChange={(v) => upd('image_url', v)} testid="mf-image" />

                    <div data-testid="mf-gallery">
                        <div className="flex items-center justify-between mb-2">
                            <label className="label-caps text-white/60">
                                Gallery ({(form.gallery || []).length} extra image{(form.gallery || []).length === 1 ? '' : 's'})
                            </label>
                            <button
                                type="button"
                                onClick={addGallery}
                                className="label-caps text-brand-primary hover:text-white transition-colors text-xs"
                                data-testid="mf-add-gallery"
                            >
                                + Paste URL
                            </button>
                        </div>

                        {/* Prominent bulk uploader — click OR drop to add multiple photos */}
                        <button
                            type="button"
                            onClick={() => bulkRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); }}
                            onDrop={(e) => { e.preventDefault(); handleBulkFiles(e.dataTransfer.files); }}
                            disabled={bulkUploading}
                            className="w-full border-2 border-dashed border-brand-primary/50 hover:border-brand-primary hover:bg-brand-primary/5 transition-colors p-6 flex flex-col items-center gap-2 text-center disabled:opacity-60"
                            data-testid="mf-gallery-bulk-upload"
                        >
                            {bulkUploading ? (
                                <>
                                    <Loader2 className="h-6 w-6 text-brand-primary animate-spin" />
                                    <div className="label-caps text-brand-primary">Uploading…</div>
                                </>
                            ) : (
                                <>
                                    <Upload className="h-6 w-6 text-brand-primary" />
                                    <div className="label-caps text-brand-primary">Click to select photos (or drop files)</div>
                                    <div className="text-xs text-white/50 normal-case font-normal">
                                        You can select 3–6 photos at once. PNG, JPG, WEBP up to 50&nbsp;MB each.
                                    </div>
                                </>
                            )}
                        </button>
                        <input
                            ref={bulkRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                            multiple
                            className="hidden"
                            onChange={(e) => handleBulkFiles(e.target.files)}
                            data-testid="mf-gallery-bulk-input"
                        />
                        {bulkError && (
                            <div className="mt-2 text-xs text-red-300" data-testid="mf-gallery-bulk-error">{bulkError}</div>
                        )}

                        {(form.gallery || []).length > 0 && (
                            <div className="mt-4 space-y-3">
                                {(form.gallery || []).map((g, i) => (
                                    <div key={i} className="grid grid-cols-[1fr_auto] gap-2 items-start border border-white/10 p-3">
                                        <ImageInput
                                            label={`Image ${i + 2}`}
                                            value={g}
                                            onChange={(v) => updGallery(i, v)}
                                            testid={`mf-gallery-${i}`}
                                        />
                                        <div className="flex flex-col gap-1 pt-6">
                                            <button
                                                type="button"
                                                onClick={() => moveGallery(i, -1)}
                                                disabled={i === 0}
                                                className="p-1.5 border border-white/10 hover:bg-white/10 transition-colors disabled:opacity-30"
                                                data-testid={`mf-gallery-up-${i}`}
                                                title="Move up"
                                            >
                                                <ArrowUp className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => moveGallery(i, 1)}
                                                disabled={i === (form.gallery.length - 1)}
                                                className="p-1.5 border border-white/10 hover:bg-white/10 transition-colors disabled:opacity-30"
                                                data-testid={`mf-gallery-down-${i}`}
                                                title="Move down"
                                            >
                                                <ArrowDown className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => removeGallery(i)}
                                                className="p-1.5 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"
                                                data-testid={`mf-gallery-remove-${i}`}
                                                title="Remove"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <TextField label="Short description" value={form.short_description} onChange={(v) => upd('short_description', v)} testid="mf-short" />
                    <TextArea label="Full description" value={form.description} onChange={(v) => upd('description', v)} testid="mf-desc" rows={4} />

                    <div className="grid grid-cols-2 gap-4 items-end">
                        <div>
                            <label className="label-caps text-white/60 flex items-center gap-2 mb-2">
                                <input type="checkbox" checked={!!form.contact_for_price} onChange={(e) => upd('contact_for_price', e.target.checked)} data-testid="mf-contact-price" />
                                Contact for Pricing
                            </label>
                        </div>
                        <TextField label="Price (USD, if not contact)" value={form.price} onChange={(v) => upd('price', v)} type="number" disabled={form.contact_for_price} testid="mf-price" />
                    </div>

                    <ShopFields form={form} upd={upd} />

                    <TextField label="External link (dryicemarketplace.com URL) — optional" value={form.external_link} onChange={(v) => upd('external_link', v)} testid="mf-ext" />
                    <div className="text-xs text-white/50 -mt-2 pl-1">
                        Tip: you can either fill in all fields manually, OR just paste the dryicemarketplace.com link and use images/description from there — both work.
                    </div>
                    <TextField label="Video embed URL (YouTube/Vimeo)" value={form.video_url} onChange={(v) => upd('video_url', v)} testid="mf-video" />

                    <TextArea label="Features (one per line)" value={form.features} onChange={(v) => upd('features', v)} testid="mf-features" rows={4} />

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="label-caps text-white/60">Specs</label>
                            <button type="button" onClick={addSpec} className="label-caps text-brand-primary hover:text-white transition-colors" data-testid="mf-add-spec">+ Add spec</button>
                        </div>
                        <div className="space-y-2">
                            {(form.specs || []).map((s, i) => (
                                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                                    <input value={s.label} onChange={(e) => updSpec(i, 'label', e.target.value)} placeholder="Label" className="bg-brand-bg border border-white/10 p-2 text-white" />
                                    <input value={s.value} onChange={(e) => updSpec(i, 'value', e.target.value)} placeholder="Value" className="bg-brand-bg border border-white/10 p-2 text-white" />
                                    <button type="button" onClick={() => removeSpec(i)} className="p-2 border border-red-500/40 text-red-300"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-6">
                        <Check label="Featured on home" checked={!!form.featured} onChange={(v) => upd('featured', v)} testid="mf-featured" />
                        <Check label="Financing available" checked={!!form.financing_available} onChange={(v) => upd('financing_available', v)} testid="mf-finance" />
                        <Check label="Published" checked={!!form.published} onChange={(v) => upd('published', v)} testid="mf-published" />
                    </div>
                </div>
                <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 bg-brand-surface flex-shrink-0">
                    <button onClick={onCancel} className="label-caps border border-white/20 hover:bg-white/10 transition-colors px-5 py-3" data-testid="admin-machine-cancel">Cancel</button>
                    <button onClick={() => onSave(form)} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-5 py-3 label-caps transition-colors" data-testid="admin-machine-save">
                        <Save className="h-4 w-4" /> Save
                    </button>
                </div>
            </div>
        </div>
    );
};

// ----- Branding ------------------------------------------------------------
const BrandingAdmin = () => {
    const { refresh } = useSiteSettings();
    const [form, setForm] = useState({
        logo_url: '',
        logo_transparent: true,
        about_chapters: [],
        about_stats: [],
        hero_video_url: '',
        hero_video_poster: '',
        before_after_items: [],
        home_copy: {},
    });
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        api.get('/admin/site-settings').then((r) => {
            let chapters = Array.isArray(r.data.about_chapters) ? r.data.about_chapters : [];
            // Backfill from about_content if chapters are missing (legacy content).
            if (chapters.length === 0 && r.data.about_content) {
                chapters = String(r.data.about_content)
                    .split(/\n\s*\n/)
                    .map((t) => t.trim())
                    .filter(Boolean)
                    .map((text) => ({ text, image_url: '' }));
            }
            setForm({
                logo_url: r.data.logo_url || '',
                logo_transparent: r.data.logo_transparent !== false,
                about_chapters: chapters,
                about_stats: Array.isArray(r.data.about_stats) ? r.data.about_stats : [],
                hero_video_url: r.data.hero_video_url || '',
                hero_video_poster: r.data.hero_video_poster || '',
                before_after_items: Array.isArray(r.data.before_after_items) ? r.data.before_after_items : [],
                home_copy: (r.data.home_copy && typeof r.data.home_copy === 'object') ? r.data.home_copy : {},
            });
            setLoaded(true);
        });
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                logo_url: form.logo_url,
                logo_transparent: form.logo_transparent,
                about_chapters: (form.about_chapters || [])
                    .filter((c) => (c.text || '').trim() || (c.image_url || '').trim())
                    .map((c) => ({ text: (c.text || '').trim(), image_url: c.image_url || '' })),
                about_stats: (form.about_stats || []).filter((s) => (s.value || '').trim() || (s.label || '').trim()),
                hero_video_url: form.hero_video_url || '',
                hero_video_poster: form.hero_video_poster || '',
                before_after_items: (form.before_after_items || [])
                    .filter((b) => (b.before_image || '').trim() && (b.after_image || '').trim())
                    .map((b) => ({
                        before_image: b.before_image || '',
                        after_image: b.after_image || '',
                        title: (b.title || '').trim(),
                        description: (b.description || '').trim(),
                    })),
                home_copy: Object.fromEntries(
                    Object.entries(form.home_copy || {}).map(([k, v]) => [k, (v || '').toString().trim()]),
                ),
            };
            await api.put('/admin/site-settings', payload);
            await refresh();
            setSavedAt(new Date());
        } finally {
            setSaving(false);
        }
    };

    // Stats helpers
    const updStat = (i, k, v) => {
        const next = [...(form.about_stats || [])];
        next[i] = { ...(next[i] || {}), [k]: v };
        setForm({ ...form, about_stats: next });
    };
    const addStat = () => setForm({ ...form, about_stats: [...(form.about_stats || []), { value: '', label: '' }] });
    const removeStat = (i) => setForm({ ...form, about_stats: form.about_stats.filter((_, idx) => idx !== i) });

    // Chapter helpers
    const updChapter = (i, k, v) => {
        const next = [...(form.about_chapters || [])];
        next[i] = { ...(next[i] || {}), [k]: v };
        setForm({ ...form, about_chapters: next });
    };
    const addChapter = () => setForm({ ...form, about_chapters: [...(form.about_chapters || []), { text: '', image_url: '' }] });
    const removeChapter = (i) => setForm({ ...form, about_chapters: form.about_chapters.filter((_, idx) => idx !== i) });
    const moveChapter = (i, dir) => {
        const next = [...(form.about_chapters || [])];
        const j = i + dir;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        setForm({ ...form, about_chapters: next });
    };

    if (!loaded) return <div className="text-white/50 label-caps">Loading…</div>;

    return (
        <div className="max-w-3xl space-y-10">
            <div>
                <h2 className="font-display font-black uppercase text-3xl mb-2">Branding & Content</h2>
                <p className="text-white/50 text-sm">
                    Everything site-wide: logo, About page chapters (each with an optional image), and the big stat numbers between them.
                </p>
            </div>

            {/* Logo */}
            <div className="border border-white/10 p-6 md:p-8 space-y-6">
                <h3 className="font-display font-black uppercase text-xl">Logo</h3>
                <ImageInput
                    label="Logo image"
                    value={form.logo_url}
                    onChange={(v) => setForm({ ...form, logo_url: v })}
                    testid="branding-logo"
                    hint="Recommended: transparent PNG or SVG, ~500 px wide."
                />
                <label className="label-caps text-white/70 flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!form.logo_transparent}
                        onChange={(e) => setForm({ ...form, logo_transparent: e.target.checked })}
                        data-testid="branding-transparent"
                    />
                    Logo has a transparent background (show directly on dark theme)
                </label>
                <div>
                    <div className="label-caps text-white/50 mb-3">Live preview</div>
                    <div className="border border-white/10 bg-brand-bg p-4 inline-block">
                        <Logo size="md" />
                    </div>
                </div>
            </div>

            {/* Hero background video */}
            <div className="border border-white/10 p-6 md:p-8 space-y-6" data-testid="branding-hero-video-block">
                <div>
                    <h3 className="font-display font-black uppercase text-xl">Homepage hero — background video</h3>
                    <p className="text-white/50 text-sm mt-1">
                        Optional. Uploads a silent looping video behind the homepage headline. Leave empty to use the default static image.
                        Recommended: 6–15 seconds, 1280×720 MP4 (H.264), under 20 MB. Max 50 MB.
                    </p>
                </div>
                <ImageInput
                    kind="video"
                    label="Hero video (MP4 / WebM / MOV)"
                    value={form.hero_video_url}
                    onChange={(v) => setForm({ ...form, hero_video_url: v })}
                    testid="branding-hero-video"
                    hint="Silent loop. No sound plays. Content should read fine at 15% opacity — we overlay the white/orange headline on top."
                />
                <ImageInput
                    label="Hero video poster (optional still frame)"
                    value={form.hero_video_poster}
                    onChange={(v) => setForm({ ...form, hero_video_poster: v })}
                    testid="branding-hero-poster"
                    hint="Shown while the video is loading. If empty we'll use the site's default fallback image."
                />
                {form.hero_video_url && (
                    <div>
                        <div className="label-caps text-white/50 mb-3">Preview</div>
                        <div className="w-full max-w-md aspect-video border border-white/10 bg-black overflow-hidden">
                            <video
                                key={form.hero_video_url}
                                src={form.hero_video_url.startsWith('http') ? form.hero_video_url : (process.env.REACT_APP_BACKEND_URL + form.hero_video_url)}
                                poster={form.hero_video_poster || undefined}
                                muted
                                loop
                                autoPlay
                                playsInline
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Before / After proof slider */}
            <div className="border border-white/10 p-6 md:p-8 space-y-4" data-testid="branding-ba-block">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <h3 className="font-display font-black uppercase text-xl">Homepage — Before / After proof</h3>
                        <p className="text-white/50 text-sm mt-1">
                            The &quot;See the difference&quot; section on the homepage. Each pair renders as a draggable slider.
                            Recommended: 2 items side-by-side; matching aspect ratios (e.g. 4:3 or 1:1). Both photos should be the same crop.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setForm({ ...form, before_after_items: [...(form.before_after_items || []), { before_image: '', after_image: '', title: '', description: '' }] })}
                        className="inline-flex items-center gap-1 label-caps text-brand-primary hover:text-white transition-colors text-xs"
                        data-testid="branding-ba-add"
                    >
                        <Plus className="h-4 w-4" /> Add pair
                    </button>
                </div>

                <div className="space-y-6">
                    {(form.before_after_items || []).map((b, i) => (
                        <div key={i} className="border border-white/10 p-5 space-y-4 bg-brand-bg" data-testid={`branding-ba-row-${i}`}>
                            <div className="flex items-center justify-between">
                                <span className="label-caps text-brand-primary">Pair {String(i + 1).padStart(2, '0')}</span>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const next = [...(form.before_after_items || [])];
                                            if (i === 0) return;
                                            [next[i - 1], next[i]] = [next[i], next[i - 1]];
                                            setForm({ ...form, before_after_items: next });
                                        }}
                                        disabled={i === 0}
                                        className="p-1.5 border border-white/10 hover:bg-white/10 transition-colors disabled:opacity-30"
                                        title="Move up"
                                        data-testid={`branding-ba-up-${i}`}
                                    >
                                        <ArrowUp className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const next = [...(form.before_after_items || [])];
                                            if (i === next.length - 1) return;
                                            [next[i], next[i + 1]] = [next[i + 1], next[i]];
                                            setForm({ ...form, before_after_items: next });
                                        }}
                                        disabled={i === (form.before_after_items.length - 1)}
                                        className="p-1.5 border border-white/10 hover:bg-white/10 transition-colors disabled:opacity-30"
                                        title="Move down"
                                        data-testid={`branding-ba-down-${i}`}
                                    >
                                        <ArrowDown className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, before_after_items: form.before_after_items.filter((_, idx) => idx !== i) })}
                                        className="p-1.5 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors ml-1"
                                        title="Delete"
                                        data-testid={`branding-ba-remove-${i}`}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <ImageInput
                                    label="Before image *"
                                    value={b.before_image || ''}
                                    onChange={(v) => {
                                        const next = [...(form.before_after_items || [])];
                                        next[i] = { ...(next[i] || {}), before_image: v };
                                        setForm({ ...form, before_after_items: next });
                                    }}
                                    testid={`branding-ba-before-${i}`}
                                    hint="Dirty / uncleaned state."
                                />
                                <ImageInput
                                    label="After image *"
                                    value={b.after_image || ''}
                                    onChange={(v) => {
                                        const next = [...(form.before_after_items || [])];
                                        next[i] = { ...(next[i] || {}), after_image: v };
                                        setForm({ ...form, before_after_items: next });
                                    }}
                                    testid={`branding-ba-after-${i}`}
                                    hint="Same angle, same crop — after cleaning."
                                />
                            </div>
                            <div>
                                <label className="label-caps text-white/60 block mb-2">Title</label>
                                <input
                                    value={b.title || ''}
                                    onChange={(e) => {
                                        const next = [...(form.before_after_items || [])];
                                        next[i] = { ...(next[i] || {}), title: e.target.value };
                                        setForm({ ...form, before_after_items: next });
                                    }}
                                    placeholder="e.g. Injection mold cleaning"
                                    className="w-full bg-brand-surface border border-white/10 focus:border-brand-primary transition-colors text-white p-3 outline-none text-sm"
                                    data-testid={`branding-ba-title-${i}`}
                                />
                            </div>
                            <div>
                                <label className="label-caps text-white/60 block mb-2">Description</label>
                                <input
                                    value={b.description || ''}
                                    onChange={(e) => {
                                        const next = [...(form.before_after_items || [])];
                                        next[i] = { ...(next[i] || {}), description: e.target.value };
                                        setForm({ ...form, before_after_items: next });
                                    }}
                                    placeholder="e.g. Dry ice blasting — carbon buildup removed, no disassembly."
                                    className="w-full bg-brand-surface border border-white/10 focus:border-brand-primary transition-colors text-white p-3 outline-none text-sm"
                                    data-testid={`branding-ba-desc-${i}`}
                                />
                            </div>
                        </div>
                    ))}
                    {(form.before_after_items || []).length === 0 && (
                        <div className="text-white/40 text-sm">
                            No pairs yet — the homepage will fall back to the default stock photos. Click &quot;Add pair&quot; to upload your real before / after shots.
                        </div>
                    )}
                </div>
            </div>

            {/* Homepage headlines & eyebrows — editable copy */}
            <div className="border border-white/10 p-6 md:p-8 space-y-5" data-testid="branding-home-copy-block">
                <div>
                    <h3 className="font-display font-black uppercase text-xl">Homepage — headlines & eyebrows</h3>
                    <p className="text-white/50 text-sm mt-1">
                        Every field here is optional. Leave a field empty to fall back to the current default copy on <span className="text-brand-primary">/</span>.
                    </p>
                </div>

                {/* Hero */}
                <div className="border border-white/5 p-4 space-y-3">
                    <div className="label-caps text-brand-primary text-xs">Hero section</div>
                    <HomeCopyField
                        label="Hero eyebrow (top ribbon)"
                        placeholder="Advanced Industrial Cleaning Technologies & Equipment"
                        field="hero_eyebrow"
                        form={form}
                        setForm={setForm}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <HomeCopyField label="Hero headline — line 1" placeholder="The right technology" field="hero_h1_line1" form={form} setForm={setForm} />
                        <HomeCopyField label="Hero headline — line 2 (before accent)" placeholder="for every" field="hero_h1_line2" form={form} setForm={setForm} />
                    </div>
                    <HomeCopyField
                        label="Hero headline — orange accent"
                        placeholder="surface challenge."
                        field="hero_h1_accent"
                        form={form}
                        setForm={setForm}
                        hint="Rendered in brand orange at the end of the H1."
                    />
                    <HomeCopyField
                        label="Hero subheading (paragraph)"
                        placeholder="Dry Ice Blasting, Laser Cleaning, Sandblasting and Industrial Pressure Washing…"
                        field="hero_sub"
                        form={form}
                        setForm={setForm}
                        multiline
                    />
                    <HomeCopyField
                        label="Hero coverage line (below subheading)"
                        placeholder="Mobile industrial service throughout North Carolina and professional equipment solutions nationwide."
                        field="hero_sub_secondary"
                        form={form}
                        setForm={setForm}
                        multiline
                    />
                </div>

                {/* Services */}
                <div className="border border-white/5 p-4 space-y-3">
                    <div className="label-caps text-brand-primary text-xs">Services section</div>
                    <HomeCopyField
                        label="Services eyebrow"
                        placeholder="/ 01 — Our services"
                        field="services_eyebrow"
                        form={form}
                        setForm={setForm}
                    />
                    <HomeCopyField
                        label="Services heading"
                        placeholder="Four cleaning technologies. One team."
                        field="services_heading"
                        form={form}
                        setForm={setForm}
                        multiline
                        hint="Press Enter to add a line-break exactly where you want it."
                    />
                </div>
            </div>


            {/* Chapters */}
            <div className="border border-white/10 p-6 md:p-8 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-display font-black uppercase text-xl">About — chapters</h3>
                        <p className="text-white/50 text-sm mt-1">
                            Each chapter is a paragraph shown on <span className="text-brand-primary">/about</span> with an optional supporting photo. Order matters — chapters and stats interleave.
                        </p>
                    </div>
                    <button type="button" onClick={addChapter} className="inline-flex items-center gap-1 label-caps text-brand-primary hover:text-white transition-colors text-xs" data-testid="branding-chapter-add">
                        <Plus className="h-4 w-4" /> Add chapter
                    </button>
                </div>

                <div className="space-y-6">
                    {(form.about_chapters || []).map((c, i) => (
                        <div key={i} className="border border-white/10 p-5 space-y-4 bg-brand-bg" data-testid={`branding-chapter-row-${i}`}>
                            <div className="flex items-center justify-between">
                                <span className="label-caps text-brand-primary">Chapter {String(i + 1).padStart(2, '0')}</span>
                                <div className="flex items-center gap-1">
                                    <button type="button" onClick={() => moveChapter(i, -1)} disabled={i === 0} className="p-1.5 border border-white/10 hover:bg-white/10 transition-colors disabled:opacity-30" title="Move up" data-testid={`branding-chapter-up-${i}`}>
                                        <ArrowUp className="h-3.5 w-3.5" />
                                    </button>
                                    <button type="button" onClick={() => moveChapter(i, 1)} disabled={i === (form.about_chapters.length - 1)} className="p-1.5 border border-white/10 hover:bg-white/10 transition-colors disabled:opacity-30" title="Move down" data-testid={`branding-chapter-down-${i}`}>
                                        <ArrowDown className="h-3.5 w-3.5" />
                                    </button>
                                    <button type="button" onClick={() => removeChapter(i)} className="p-1.5 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors ml-1" title="Delete" data-testid={`branding-chapter-remove-${i}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                            <textarea
                                value={c.text || ''}
                                onChange={(e) => updChapter(i, 'text', e.target.value)}
                                rows={5}
                                placeholder="Chapter text — becomes one big paragraph on /about."
                                className="w-full bg-brand-surface border border-white/10 focus:border-brand-primary transition-colors text-white p-3 outline-none text-sm leading-relaxed"
                                data-testid={`branding-chapter-text-${i}`}
                            />
                            <ImageInput
                                label="Chapter image (optional)"
                                value={c.image_url || ''}
                                onChange={(v) => updChapter(i, 'image_url', v)}
                                testid={`branding-chapter-image-${i}`}
                                hint="If set, the paragraph shows next to this photo (magazine spread). Leave empty for text-only."
                            />
                        </div>
                    ))}
                    {(form.about_chapters || []).length === 0 && (
                        <div className="text-white/40 text-sm">No chapters yet — click &quot;Add chapter&quot; to create one.</div>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="border border-white/10 p-6 md:p-8 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-display font-black uppercase text-xl">About — big stats</h3>
                        <p className="text-white/50 text-sm mt-1">Displayed between chapters on <span className="text-brand-primary">/about</span>. Extras appear as a grid at the bottom.</p>
                    </div>
                    <button type="button" onClick={addStat} className="inline-flex items-center gap-1 label-caps text-brand-primary hover:text-white transition-colors text-xs" data-testid="branding-stat-add">
                        <Plus className="h-4 w-4" /> Add stat
                    </button>
                </div>
                <div className="space-y-3">
                    {(form.about_stats || []).map((s, i) => (
                        <div key={i} className="grid grid-cols-[9rem_1fr_auto] gap-2" data-testid={`branding-stat-row-${i}`}>
                            <input value={s.value || ''} onChange={(e) => updStat(i, 'value', e.target.value)} placeholder="27+" className="bg-brand-bg border border-white/10 focus:border-brand-primary text-white p-2.5 outline-none font-display font-black text-lg" data-testid={`branding-stat-value-${i}`} />
                            <input value={s.label || ''} onChange={(e) => updStat(i, 'label', e.target.value)} placeholder="Years of expertise" className="bg-brand-bg border border-white/10 focus:border-brand-primary text-white p-2.5 outline-none text-sm" data-testid={`branding-stat-label-${i}`} />
                            <button type="button" onClick={() => removeStat(i)} className="p-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors" data-testid={`branding-stat-remove-${i}`} aria-label="remove">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                    {(form.about_stats || []).length === 0 && (
                        <div className="text-white/40 text-sm">No stats yet — click &quot;Add stat&quot; to create one.</div>
                    )}
                </div>
            </div>

            {/* Save */}
            <div className="flex items-center gap-3">
                <button
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-5 py-3 label-caps transition-colors disabled:opacity-60"
                    data-testid="branding-save"
                >
                    <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save all'}
                </button>
                {savedAt && <span className="text-brand-primary label-caps text-xs">Saved ✓</span>}
            </div>
        </div>
    );
};

// ----- Services ------------------------------------------------------------
const ServicesAdmin = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = async () => { const { data } = await api.get('/admin/services'); setItems(data); };
    useEffect(() => { load(); }, []);

    const save = async (form) => {
        const payload = {
            ...form,
            bullets: (Array.isArray(form.bullets) ? form.bullets : String(form.bullets || '').split('\n')).map((s) => s.trim()).filter(Boolean),
            order: parseInt(form.order || 0, 10) || 0,
            video_url: form.video_url || '',
        };
        await api.put(`/admin/services/${form.key}`, payload);
        setEditing(null); load();
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display font-black uppercase text-3xl">Services</h2>
                    <p className="text-white/50 text-sm mt-1">Edit the image, title, description and bullets shown on the Services page.</p>
                </div>
            </div>

            <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-services-list">
                {items.length === 0 && <div className="p-6 text-white/50">Loading services…</div>}
                {items.map((s) => (
                    <div key={s.key} className="p-4 flex items-center gap-4">
                        <div className="w-24 h-16 bg-brand-surface2 flex-shrink-0 border border-white/10 overflow-hidden">
                            {s.image_url ? <img src={s.image_url} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-white truncate">{s.title}</div>
                            <div className="text-sm text-white/50 line-clamp-1">{s.blurb}</div>
                        </div>
                        <button
                            onClick={() => setEditing({ ...s, bullets: (s.bullets || []).join('\n') })}
                            className="p-2 border border-white/10 hover:bg-white/10"
                            data-testid={`admin-service-edit-${s.key}`}
                        >
                            <Pencil className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>

            {editing && (
                <Modal title={`Edit — ${editing.title}`} onClose={() => setEditing(null)} onSave={() => save(editing)} testid="admin-service-modal">
                    <TextField label="Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="sf-title" />
                    <TextField
                        label="Homepage tag chip"
                        value={editing.home_tag || ''}
                        onChange={(v) => setEditing({ ...editing, home_tag: v })}
                        testid="sf-home-tag"
                        placeholder="e.g. Zero residue"
                    />
                    <div className="text-white/40 text-xs -mt-2">
                        Short label shown in the top-left of this service&apos;s card on the homepage. Keep it to 2–3 words (e.g. &quot;Zero residue&quot;, &quot;Heavy corrosion&quot;, &quot;High volume&quot;, &quot;Contact-free&quot;). Leave empty to hide the chip.
                    </div>
                    <ImageInput
                        label="Service image (used as fallback / video poster)"
                        value={editing.image_url}
                        onChange={(v) => setEditing({ ...editing, image_url: v })}
                        testid="sf-image"
                    />
                    <ImageInput
                        kind="video"
                        label="Looping video (auto-plays silently in this section)"
                        value={editing.video_url}
                        onChange={(v) => setEditing({ ...editing, video_url: v })}
                        testid="sf-video"
                        hint="Ideal: 8–15s silent MP4 (H.264, 720p, ~5–15 MB). WebM and MOV also work. Max 50 MB."
                    />
                    <TextArea label="Description" value={editing.blurb} onChange={(v) => setEditing({ ...editing, blurb: v })} testid="sf-blurb" rows={3} />
                    <TextArea label="Bullet points (one per line)" value={editing.bullets} onChange={(v) => setEditing({ ...editing, bullets: v })} testid="sf-bullets" rows={6} />
                    <TextField label="Display order" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} testid="sf-order" />
                </Modal>
            )}
        </div>
    );
};

// ----- Videos --------------------------------------------------------------
const VideosAdmin = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = async () => { const { data } = await api.get('/admin/videos'); setItems(data); };
    useEffect(() => { load(); }, []);

    const save = async (form) => {
        if (form.id) await api.put(`/admin/videos/${form.id}`, form);
        else await api.post('/admin/videos', form);
        setEditing(null); load();
    };
    const remove = async (id) => { if (!window.confirm('Delete video?')) return; await api.delete(`/admin/videos/${id}`); load(); };

    const emptyV = { title: '', description: '', video_url: '', thumbnail_url: '', category: 'general', featured: false, published: true };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display font-black uppercase text-3xl">Videos</h2>
                <button onClick={() => setEditing({ ...emptyV })} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps transition-colors" data-testid="admin-videos-new">
                    <Plus className="h-4 w-4" /> New Video
                </button>
            </div>

            <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-videos-list">
                {items.length === 0 && <div className="p-6 text-white/50">No videos yet.</div>}
                {items.map((v) => (
                    <div key={v.id} className="p-4 flex items-center gap-4">
                        <div className="w-24 h-14 bg-brand-surface2 flex-shrink-0 border border-white/10 overflow-hidden">
                            {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-white truncate flex items-center gap-2">{v.title} {v.featured && <Star className="h-4 w-4 text-brand-primary" />}</div>
                            <div className="text-sm text-white/50 truncate">{v.video_url}</div>
                        </div>
                        <button onClick={() => setEditing(v)} className="p-2 border border-white/10 hover:bg-white/10" data-testid={`admin-video-edit-${v.id}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(v.id)} className="p-2 border border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`admin-video-delete-${v.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                ))}
            </div>

            {editing && (
                <Modal title={`${editing.id ? 'Edit' : 'New'} Video`} onClose={() => setEditing(null)} onSave={() => save(editing)} testid="admin-video-modal">
                    <TextField label="Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="vf-title" />
                    <TextField label="Video embed URL * (YouTube/Vimeo embed)" value={editing.video_url} onChange={(v) => setEditing({ ...editing, video_url: v })} testid="vf-url" />
                    <TextField label="Thumbnail URL" value={editing.thumbnail_url} onChange={(v) => setEditing({ ...editing, thumbnail_url: v })} testid="vf-thumb" />
                    <TextArea label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="vf-desc" rows={3} />
                    <div className="flex gap-6">
                        <Check label="Featured" checked={!!editing.featured} onChange={(v) => setEditing({ ...editing, featured: v })} testid="vf-featured" />
                        <Check label="Published" checked={!!editing.published} onChange={(v) => setEditing({ ...editing, published: v })} testid="vf-published" />
                    </div>
                </Modal>
            )}
        </div>
    );
};

// ----- News ----------------------------------------------------------------
const NewsAdmin = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = async () => { const { data } = await api.get('/admin/news'); setItems(data); };
    useEffect(() => { load(); }, []);

    const save = async (form) => {
        const payload = { ...form, tags: (Array.isArray(form.tags) ? form.tags : String(form.tags || '').split(',')).map((s) => s.trim()).filter(Boolean) };
        if (form.id) await api.put(`/admin/news/${form.id}`, payload);
        else await api.post('/admin/news', payload);
        setEditing(null); load();
    };
    const remove = async (id) => { if (!window.confirm('Delete article?')) return; await api.delete(`/admin/news/${id}`); load(); };

    const emptyN = { title: '', excerpt: '', content: '', cover_image: '', author: 'Dry Ice Bulls', tags: '', published: true };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display font-black uppercase text-3xl">News</h2>
                <button onClick={() => setEditing({ ...emptyN })} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps transition-colors" data-testid="admin-news-new">
                    <Plus className="h-4 w-4" /> New Article
                </button>
            </div>

            <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-news-list">
                {items.length === 0 && <div className="p-6 text-white/50">No articles yet.</div>}
                {items.map((n) => (
                    <div key={n.id} className="p-4 flex items-center gap-4">
                        <div className="w-20 h-14 bg-brand-surface2 flex-shrink-0 border border-white/10 overflow-hidden">
                            {n.cover_image ? <img src={n.cover_image} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-white truncate">{n.title}</div>
                            <div className="text-sm text-white/50 truncate">{n.excerpt}</div>
                        </div>
                        <button onClick={() => setEditing({ ...n, tags: (n.tags || []).join(', ') })} className="p-2 border border-white/10 hover:bg-white/10" data-testid={`admin-news-edit-${n.id}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(n.id)} className="p-2 border border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`admin-news-delete-${n.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                ))}
            </div>

            {editing && (
                <Modal title={`${editing.id ? 'Edit' : 'New'} Article`} onClose={() => setEditing(null)} onSave={() => save(editing)} testid="admin-news-modal">
                    <TextField label="Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="nf-title" />
                    <ImageInput label="Cover image" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} testid="nf-cover" />
                    <TextField label="Author" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} testid="nf-author" />
                    <TextField label="Tags (comma separated)" value={editing.tags} onChange={(v) => setEditing({ ...editing, tags: v })} testid="nf-tags" />
                    <TextArea label="Excerpt" value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} testid="nf-excerpt" rows={2} />
                    <TextArea label="Content" value={editing.content} onChange={(v) => setEditing({ ...editing, content: v })} testid="nf-content" rows={8} />
                    <Check label="Published" checked={!!editing.published} onChange={(v) => setEditing({ ...editing, published: v })} testid="nf-published" />
                </Modal>
            )}
        </div>
    );
};

// ----- Projects (Project Gallery / Case Studies) ---------------------------
const CATS = [
    { key: 'dry-ice', label: 'Dry Ice Blasting' },
    { key: 'laser', label: 'Laser Cleaning' },
    { key: 'sandblasting', label: 'Sandblasting' },
    { key: 'pressure-washing', label: 'Pressure Washing' },
    { key: 'mixed', label: 'Mixed Services' },
];

const ProjectsAdmin = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = async () => { const { data } = await api.get('/admin/projects'); setItems(data); };
    useEffect(() => { load(); }, []);

    const save = async (form) => {
        const payload = {
            ...form,
            order: parseInt(form.order || 0, 10) || 0,
            images: (form.images || []).map((s) => (s || '').trim()).filter(Boolean),
            videos: (form.videos || []).map((s) => (s || '').trim()).filter(Boolean),
        };
        if (form.id) await api.put(`/admin/projects/${form.id}`, payload);
        else await api.post('/admin/projects', payload);
        setEditing(null); load();
    };
    const remove = async (id) => { if (!window.confirm('Delete project?')) return; await api.delete(`/admin/projects/${id}`); load(); };

    const emptyP = { title: '', category: 'dry-ice', summary: '', description: '', cover_image: '', images: [], videos: [], featured: false, published: true, order: 0 };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display font-black uppercase text-3xl">Project Gallery</h2>
                    <p className="text-white/50 text-sm mt-1">Case studies visible at /projects. Add multiple images and video URLs (YouTube/Vimeo) per project.</p>
                </div>
                <button onClick={() => setEditing({ ...emptyP })} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps transition-colors" data-testid="admin-projects-new">
                    <Plus className="h-4 w-4" /> New Project
                </button>
            </div>

            <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-projects-list">
                {items.length === 0 && <div className="p-6 text-white/50">No projects yet.</div>}
                {items.map((p) => (
                    <div key={p.id} className="p-4 flex items-center gap-4">
                        <div className="w-20 h-14 bg-brand-surface2 flex-shrink-0 border border-white/10 overflow-hidden">
                            {p.cover_image ? <img src={p.cover_image} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-white truncate">{p.title}</div>
                            <div className="text-sm text-white/50">
                                {(CATS.find((c) => c.key === p.category)?.label) || p.category}
                                {' · '}{(p.images || []).length} img
                                {' · '}{(p.videos || []).length} vid
                                {!p.published && ' · Hidden'}
                            </div>
                        </div>
                        <button onClick={() => setEditing({ ...p, images: p.images || [], videos: p.videos || [] })} className="p-2 border border-white/10 hover:bg-white/10" data-testid={`admin-project-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(p.id)} className="p-2 border border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`admin-project-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                ))}
            </div>

            {editing && (
                <ProjectForm
                    initial={editing}
                    onCancel={() => setEditing(null)}
                    onSave={save}
                />
            )}
        </div>
    );
};

const ProjectForm = ({ initial, onCancel, onSave }) => {
    const [form, setForm] = useState({ ...initial });
    const bulkRef = useRef(null);
    const [bulkUploading, setBulkUploading] = useState(false);
    const [bulkError, setBulkError] = useState('');

    const upd = (k, v) => setForm({ ...form, [k]: v });

    // Images helpers
    const updImg = (i, v) => { const n = [...(form.images || [])]; n[i] = v; setForm({ ...form, images: n }); };
    const addImg = () => setForm({ ...form, images: [...(form.images || []), ''] });
    const rmImg = (i) => setForm({ ...form, images: (form.images || []).filter((_, idx) => idx !== i) });
    const mvImg = (i, dir) => {
        const arr = [...(form.images || [])]; const j = i + dir;
        if (j < 0 || j >= arr.length) return;
        [arr[i], arr[j]] = [arr[j], arr[i]]; setForm({ ...form, images: arr });
    };
    const handleBulkFiles = async (fileList) => {
        const files = Array.from(fileList || []); if (files.length === 0) return;
        setBulkError(''); setBulkUploading(true);
        try {
            const results = await Promise.all(files.map(async (file) => {
                const fd = new FormData(); fd.append('file', file);
                try { const { data } = await api.post('/admin/upload', fd); return { url: data.url }; }
                catch (err) {
                    const detail = err.response?.data?.detail;
                    return { error: typeof detail === 'string' ? detail : `Failed: ${file.name}` };
                }
            }));
            const urls = results.filter((r) => r.url).map((r) => r.url);
            const errors = results.filter((r) => r.error).map((r) => r.error);
            setForm((prev) => ({ ...prev, images: [...(prev.images || []), ...urls] }));
            if (errors.length) setBulkError(errors.join(' • '));
        } finally { setBulkUploading(false); if (bulkRef.current) bulkRef.current.value = ''; }
    };

    // Videos helpers (URL only — YouTube/Vimeo embed or a self-hosted mp4)
    const updVid = (i, v) => { const n = [...(form.videos || [])]; n[i] = v; setForm({ ...form, videos: n }); };
    const addVid = () => setForm({ ...form, videos: [...(form.videos || []), ''] });
    const rmVid = (i) => setForm({ ...form, videos: (form.videos || []).filter((_, idx) => idx !== i) });

    const vidBulkRef = useRef(null);
    const [vidUploading, setVidUploading] = useState(false);
    const handleVideoFiles = async (fileList) => {
        const files = Array.from(fileList || []); if (files.length === 0) return;
        setVidUploading(true);
        try {
            const results = await Promise.all(files.map(async (file) => {
                const fd = new FormData(); fd.append('file', file);
                try { const { data } = await api.post('/admin/upload', fd); return data.url; }
                catch { return null; }
            }));
            const urls = results.filter(Boolean);
            setForm((prev) => ({ ...prev, videos: [...(prev.videos || []), ...urls] }));
        } finally { setVidUploading(false); if (vidBulkRef.current) vidBulkRef.current.value = ''; }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-project-modal">
            <div className="w-full max-w-3xl bg-brand-surface border border-white/10 flex flex-col max-h-[calc(100vh-2rem)]">
                <div className="flex items-center justify-between p-6 border-b border-white/10 bg-brand-surface flex-shrink-0">
                    <h3 className="font-display font-black uppercase text-2xl">{form.id ? 'Edit' : 'New'} Project</h3>
                    <button onClick={onCancel} className="text-white/60 hover:text-white"><X /></button>
                </div>
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                    <TextField label="Title *" value={form.title} onChange={(v) => upd('title', v)} testid="pf-title" />
                    <div>
                        <label className="label-caps text-white/60 block mb-2">Category</label>
                        <select
                            value={form.category}
                            onChange={(e) => upd('category', e.target.value)}
                            className="w-full bg-brand-surface2 border border-white/10 px-4 py-3 text-white focus:border-brand-primary focus:outline-none"
                            data-testid="pf-category"
                        >
                            {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                    </div>
                    <ImageInput label="Cover image *" value={form.cover_image} onChange={(v) => upd('cover_image', v)} testid="pf-cover" />
                    <TextArea label="Summary (shown on project cards + at top of detail)" value={form.summary} onChange={(v) => upd('summary', v)} testid="pf-summary" rows={2} />
                    <TextArea label="Description (full write-up)" value={form.description} onChange={(v) => upd('description', v)} testid="pf-description" rows={8} />

                    {/* Bulk images */}
                    <div data-testid="pf-images">
                        <div className="flex items-center justify-between mb-2">
                            <label className="label-caps text-white/60">
                                Images ({(form.images || []).length} extra)
                            </label>
                            <button type="button" onClick={addImg} className="label-caps text-brand-primary hover:text-white transition-colors text-xs" data-testid="pf-add-image">+ Paste URL</button>
                        </div>
                        <button
                            type="button"
                            onClick={() => bulkRef.current?.click()}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => { e.preventDefault(); handleBulkFiles(e.dataTransfer.files); }}
                            disabled={bulkUploading}
                            className="w-full border-2 border-dashed border-brand-primary/50 hover:border-brand-primary hover:bg-brand-primary/5 transition-colors p-6 flex flex-col items-center gap-2 text-center disabled:opacity-60"
                            data-testid="pf-images-bulk"
                        >
                            {bulkUploading ? (
                                <><Loader2 className="h-6 w-6 text-brand-primary animate-spin" /><div className="label-caps text-brand-primary">Uploading…</div></>
                            ) : (
                                <>
                                    <Upload className="h-6 w-6 text-brand-primary" />
                                    <div className="label-caps text-brand-primary">Click to select photos (or drop files)</div>
                                    <div className="text-xs text-white/50 normal-case font-normal">Add before/after shots, job site photos. PNG/JPG/WEBP up to 50 MB each.</div>
                                </>
                            )}
                        </button>
                        <input ref={bulkRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/avif" multiple className="hidden" onChange={(e) => handleBulkFiles(e.target.files)} />
                        {bulkError && <div className="mt-2 text-xs text-red-300">{bulkError}</div>}

                        {(form.images || []).length > 0 && (
                            <div className="mt-4 space-y-3">
                                {(form.images || []).map((g, i) => (
                                    <div key={i} className="grid grid-cols-[1fr_auto] gap-2 items-start border border-white/10 p-3">
                                        <ImageInput label={`Image ${i + 1}`} value={g} onChange={(v) => updImg(i, v)} testid={`pf-image-${i}`} />
                                        <div className="flex flex-col gap-1 pt-6">
                                            <button type="button" onClick={() => mvImg(i, -1)} disabled={i === 0} className="p-1.5 border border-white/10 hover:bg-white/10 disabled:opacity-30" title="Up"><ArrowUp className="h-3.5 w-3.5" /></button>
                                            <button type="button" onClick={() => mvImg(i, 1)} disabled={i === (form.images.length - 1)} className="p-1.5 border border-white/10 hover:bg-white/10 disabled:opacity-30" title="Down"><ArrowDown className="h-3.5 w-3.5" /></button>
                                            <button type="button" onClick={() => rmImg(i)} className="p-1.5 border border-red-500/40 text-red-300 hover:bg-red-500/10" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Videos */}
                    <div data-testid="pf-videos">
                        <div className="flex items-center justify-between mb-2">
                            <label className="label-caps text-white/60">
                                Videos ({(form.videos || []).length})
                            </label>
                            <div className="flex items-center gap-3">
                                <button type="button" onClick={() => vidBulkRef.current?.click()} disabled={vidUploading} className="label-caps text-xs text-brand-primary hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="pf-videos-upload">
                                    {vidUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                                    {vidUploading ? 'Uploading' : 'Upload MP4'}
                                </button>
                                <button type="button" onClick={addVid} className="label-caps text-brand-primary hover:text-white transition-colors text-xs" data-testid="pf-add-video">+ Paste URL</button>
                            </div>
                        </div>
                        <input ref={vidBulkRef} type="file" accept="video/mp4,video/webm,video/quicktime" multiple className="hidden" onChange={(e) => handleVideoFiles(e.target.files)} />
                        <div className="text-xs text-white/40 mb-2">Paste YouTube / Vimeo URL, or upload MP4/WEBM directly. Max 50 MB per file.</div>
                        {(form.videos || []).length === 0 ? (
                            <div className="text-xs text-white/40 border border-dashed border-white/10 p-4">No videos yet — optional. Add YouTube/Vimeo links or upload short walkthrough clips.</div>
                        ) : (
                            <div className="space-y-2">
                                {(form.videos || []).map((v, i) => (
                                    <div key={i} className="flex gap-2 items-center border border-white/10 p-2">
                                        <input
                                            type="text"
                                            value={v}
                                            onChange={(e) => updVid(i, e.target.value)}
                                            placeholder="https://youtube.com/watch?v=... or /api/uploads/x.mp4"
                                            className="flex-1 bg-brand-surface2 border border-white/10 px-3 py-2 text-white text-sm focus:border-brand-primary focus:outline-none"
                                            data-testid={`pf-video-${i}`}
                                        />
                                        <button type="button" onClick={() => rmVid(i)} className="p-2 border border-red-500/40 text-red-300 hover:bg-red-500/10" title="Remove" data-testid={`pf-video-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <TextField label="Display order" type="number" value={form.order} onChange={(v) => upd('order', v)} testid="pf-order" />
                        <div className="flex items-end gap-6">
                            <Check label="Featured" checked={!!form.featured} onChange={(v) => upd('featured', v)} testid="pf-featured" />
                            <Check label="Published" checked={!!form.published} onChange={(v) => upd('published', v)} testid="pf-published" />
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 bg-brand-surface flex-shrink-0">
                    <button onClick={onCancel} className="label-caps border border-white/20 hover:bg-white/10 transition-colors px-5 py-3" data-testid="admin-project-cancel">Cancel</button>
                    <button onClick={() => onSave(form)} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-5 py-3 label-caps transition-colors" data-testid="admin-project-save">
                        <Save className="h-4 w-4" /> Save
                    </button>
                </div>
            </div>
        </div>
    );
};

// ----- Testimonials --------------------------------------------------------
const TestimonialsAdmin = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = async () => { const { data } = await api.get('/admin/testimonials'); setItems(data); };
    useEffect(() => { load(); }, []);

    const save = async (form) => {
        const payload = { ...form, order: parseInt(form.order || 0, 10) || 0 };
        if (form.id) await api.put(`/admin/testimonials/${form.id}`, payload);
        else await api.post('/admin/testimonials', payload);
        setEditing(null); load();
    };
    const remove = async (id) => { if (!window.confirm('Delete testimonial?')) return; await api.delete(`/admin/testimonials/${id}`); load(); };

    const emptyT = { quote: '', author: '', role: '', company: '', order: 0, published: true };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display font-black uppercase text-3xl">Testimonials</h2>
                    <p className="text-white/50 text-sm mt-1">Appear as an auto-scrolling strip on the home page. Hover pauses the animation.</p>
                </div>
                <button onClick={() => setEditing({ ...emptyT })} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps transition-colors" data-testid="admin-testimonials-new">
                    <Plus className="h-4 w-4" /> New Testimonial
                </button>
            </div>

            <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-testimonials-list">
                {items.length === 0 && <div className="p-6 text-white/50">No testimonials yet.</div>}
                {items.map((t) => (
                    <div key={t.id} className="p-4 flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-white truncate">&ldquo;{t.quote}&rdquo;</div>
                            <div className="text-sm text-white/50 mt-1">
                                {t.author || 'Anonymous'}{t.role ? ` · ${t.role}` : ''}{t.company ? ` · ${t.company}` : ''}
                                {' · '}order: {t.order}
                                {!t.published && ' · Hidden'}
                            </div>
                        </div>
                        <button onClick={() => setEditing(t)} className="p-2 border border-white/10 hover:bg-white/10 transition-colors" data-testid={`admin-testimonial-edit-${t.id}`}>
                            <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => remove(t.id)} className="p-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors" data-testid={`admin-testimonial-delete-${t.id}`}>
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>

            {editing && (
                <Modal title={`${editing.id ? 'Edit' : 'New'} Testimonial`} onClose={() => setEditing(null)} onSave={() => save(editing)} testid="admin-testimonial-modal">
                    <TextArea label="Quote *" value={editing.quote} onChange={(v) => setEditing({ ...editing, quote: v })} testid="tf-quote" rows={4} />
                    <div className="grid grid-cols-2 gap-4">
                        <TextField label="Author name" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} testid="tf-author" />
                        <TextField label="Company" value={editing.company} onChange={(v) => setEditing({ ...editing, company: v })} testid="tf-company" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <TextField label="Role / title" value={editing.role} onChange={(v) => setEditing({ ...editing, role: v })} testid="tf-role" />
                        <TextField label="Display order" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} testid="tf-order" />
                    </div>
                    <Check label="Published" checked={!!editing.published} onChange={(v) => setEditing({ ...editing, published: v })} testid="tf-published" />
                </Modal>
            )}
        </div>
    );
};

// ----- Why Us --------------------------------------------------------------
const WhyUsAdmin = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = async () => { const { data } = await api.get('/admin/why-us'); setItems(data); };
    useEffect(() => { load(); }, []);

    const save = async (form) => {
        const payload = { ...form, order: parseInt(form.order || 0, 10) || 0 };
        if (form.id) await api.put(`/admin/why-us/${form.id}`, payload);
        else await api.post('/admin/why-us', payload);
        setEditing(null); load();
    };
    const remove = async (id) => { if (!window.confirm('Delete this item?')) return; await api.delete(`/admin/why-us/${id}`); load(); };

    const emptyW = { title: '', description: '', order: 0, published: true };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display font-black uppercase text-3xl">Why Us</h2>
                    <p className="text-white/50 text-sm mt-1">The auto-scrolling strip on the home page. Hover pauses the animation.</p>
                </div>
                <button onClick={() => setEditing({ ...emptyW })} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps transition-colors" data-testid="admin-why-us-new">
                    <Plus className="h-4 w-4" /> New Item
                </button>
            </div>

            <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-why-us-list">
                {items.length === 0 && <div className="p-6 text-white/50">No items yet.</div>}
                {items.map((w) => (
                    <div key={w.id} className="p-4 flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-white truncate">{w.title}</div>
                            <div className="text-sm text-white/50 mt-1 line-clamp-2">{w.description}</div>
                            <div className="text-xs text-white/40 mt-1">order: {w.order}{!w.published && ' · Hidden'}</div>
                        </div>
                        <button onClick={() => setEditing(w)} className="p-2 border border-white/10 hover:bg-white/10 transition-colors" data-testid={`admin-why-us-edit-${w.id}`}>
                            <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => remove(w.id)} className="p-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors" data-testid={`admin-why-us-delete-${w.id}`}>
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>

            {editing && (
                <Modal title={`${editing.id ? 'Edit' : 'New'} Item`} onClose={() => setEditing(null)} onSave={() => save(editing)} testid="admin-why-us-modal">
                    <TextField label="Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="wf-title" />
                    <TextArea label="Description *" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="wf-description" rows={6} />
                    <TextField label="Display order" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} testid="wf-order" />
                    <Check label="Published" checked={!!editing.published} onChange={(v) => setEditing({ ...editing, published: v })} testid="wf-published" />
                </Modal>
            )}
        </div>
    );
};

// ----- Contacts ------------------------------------------------------------
const ContactsAdmin = () => {
    const [items, setItems] = useState([]);
    const load = async () => { const { data } = await api.get('/admin/contacts'); setItems(data); };
    useEffect(() => { load(); }, []);

    const markRead = async (id) => { await api.post(`/admin/contacts/${id}/read`); load(); };
    const remove = async (id) => { if (!window.confirm('Delete this message?')) return; await api.delete(`/admin/contacts/${id}`); load(); };

    return (
        <div>
            <h2 className="font-display font-black uppercase text-3xl mb-6">Contact submissions</h2>
            <div className="space-y-3" data-testid="admin-contacts-list">
                {items.length === 0 && <div className="p-6 text-white/50 border border-white/10">No messages yet.</div>}
                {items.map((c) => (
                    <div key={c.id} className={`border p-5 ${c.is_read ? 'border-white/10' : 'border-brand-primary bg-brand-primary/5'}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="font-display font-bold text-white text-lg">{c.name}{c.company ? ` — ${c.company}` : ''}</div>
                                <div className="text-sm text-white/60 mt-1 flex flex-wrap gap-x-4">
                                    <a href={`tel:${c.phone}`} className="hover:text-white">📞 {c.phone}</a>
                                    <a href={`mailto:${c.email}`} className="hover:text-white">✉ {c.email}</a>
                                    <span>{new Date(c.created_at).toLocaleString()}</span>
                                </div>
                                {c.subject && <div className="mt-3 label-caps text-brand-primary">{c.subject}</div>}
                                <p className="mt-2 text-white/80 whitespace-pre-line">{c.message}</p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                {!c.is_read && (
                                    <button onClick={() => markRead(c.id)} className="p-2 border border-white/10 hover:bg-white/10" data-testid={`admin-contact-read-${c.id}`} title="Mark as read"><Mail className="h-4 w-4" /></button>
                                )}
                                <button onClick={() => remove(c.id)} className="p-2 border border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`admin-contact-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ----- Shared UI -----------------------------------------------------------
// ----- Account (change email / password) ----------------------------------
const AccountAdmin = () => {
    const { admin } = useAuth();
    const [form, setForm] = useState({
        current_password: '',
        new_email: '',
        new_password: '',
        confirm_password: '',
    });
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [err, setErr] = useState('');

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setMsg(null); setErr('');
        if (!form.current_password) {
            setErr('Enter your current password to confirm the change.');
            return;
        }
        if (form.new_password && form.new_password !== form.confirm_password) {
            setErr('New password and confirmation do not match.');
            return;
        }
        if (form.new_password && form.new_password.length < 8) {
            setErr('New password must be at least 8 characters.');
            return;
        }
        if (!form.new_email && !form.new_password) {
            setErr('Enter a new email or a new password (or both).');
            return;
        }
        setSaving(true);
        try {
            const r = await api.post('/auth/change-credentials', {
                current_password: form.current_password,
                new_email: form.new_email || undefined,
                new_password: form.new_password || undefined,
            });
            setMsg(`Saved. You will use ${r.data.email} from now on. Log out and back in to refresh your session.`);
            setForm({ current_password: '', new_email: '', new_password: '', confirm_password: '' });
        } catch (e2) {
            const detail = e2.response?.data?.detail;
            setErr(typeof detail === 'string' ? detail : 'Could not save changes.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="account-admin" className="max-w-2xl">
            <div className="mb-6">
                <h2 className="font-display font-black uppercase text-3xl">Account</h2>
                <p className="text-white/50 text-sm mt-1">
                    Change your admin login email or password. You&apos;re currently signed in as <span className="text-brand-primary font-mono">{admin?.email || '—'}</span>.
                </p>
            </div>
            <form onSubmit={submit} className="border border-white/10 bg-brand-surface p-6 md:p-8 space-y-5" data-testid="account-form">
                <div>
                    <label className="label-caps text-white/60 block mb-2 text-xs">Current password <span className="text-brand-primary">*</span></label>
                    <input
                        type="password"
                        autoComplete="current-password"
                        value={form.current_password}
                        onChange={upd('current_password')}
                        className="w-full bg-brand-surface2 border border-white/10 focus:border-brand-primary transition-colors text-white p-3 outline-none text-sm"
                        data-testid="acc-current"
                        required
                    />
                    <div className="text-white/40 text-[11px] mt-1.5">Required for any change — proves it&apos;s really you.</div>
                </div>

                <div className="border-t border-white/10 pt-5">
                    <div className="label-caps text-brand-primary text-xs mb-3">/ Change email (optional)</div>
                    <input
                        type="email"
                        value={form.new_email}
                        onChange={upd('new_email')}
                        placeholder={admin?.email || 'e.g. nc@dryicebulls.com'}
                        className="w-full bg-brand-surface2 border border-white/10 focus:border-brand-primary transition-colors text-white p-3 outline-none text-sm"
                        data-testid="acc-new-email"
                    />
                </div>

                <div className="border-t border-white/10 pt-5 space-y-3">
                    <div className="label-caps text-brand-primary text-xs">/ Change password (optional)</div>
                    <input
                        type="password"
                        autoComplete="new-password"
                        value={form.new_password}
                        onChange={upd('new_password')}
                        placeholder="New password (min 8 characters)"
                        className="w-full bg-brand-surface2 border border-white/10 focus:border-brand-primary transition-colors text-white p-3 outline-none text-sm"
                        data-testid="acc-new-password"
                    />
                    <input
                        type="password"
                        autoComplete="new-password"
                        value={form.confirm_password}
                        onChange={upd('confirm_password')}
                        placeholder="Confirm new password"
                        className="w-full bg-brand-surface2 border border-white/10 focus:border-brand-primary transition-colors text-white p-3 outline-none text-sm"
                        data-testid="acc-confirm-password"
                    />
                    <div className="text-white/40 text-[11px]">
                        Use something unique — not reused from another account. A password manager makes this painless.
                    </div>
                </div>

                {err && <div className="border border-red-500/40 bg-red-500/10 text-red-300 p-3 text-sm" data-testid="acc-err">{err}</div>}
                {msg && <div className="border border-brand-primary/40 bg-brand-primary/10 text-brand-primary p-3 text-sm" data-testid="acc-ok">{msg}</div>}

                <div className="flex items-center gap-3">
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-brand-ink font-bold px-6 py-3 label-caps text-sm transition-colors"
                        data-testid="acc-submit"
                    >
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};


// ----- Careers -------------------------------------------------------------
const emptyJob = {
    title: '',
    department: '',
    location: 'North Carolina',
    employment_type: 'Full-time',
    salary: '',
    description: '',
    requirements: [],
    benefits: [],
    open: true,
    order: 0,
};

const CareersAdmin = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/jobs');
            setItems(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const save = async (form) => {
        const payload = {
            ...form,
            order: parseInt(form.order || 0, 10) || 0,
            requirements: (Array.isArray(form.requirements) ? form.requirements : String(form.requirements || '').split('\n'))
                .map((s) => s.trim()).filter(Boolean),
            benefits: (Array.isArray(form.benefits) ? form.benefits : String(form.benefits || '').split('\n'))
                .map((s) => s.trim()).filter(Boolean),
        };
        if (form.id) await api.put(`/admin/jobs/${form.id}`, payload);
        else await api.post('/admin/jobs', payload);
        setEditing(null); load();
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this job posting?')) return;
        await api.delete(`/admin/jobs/${id}`);
        load();
    };

    const toggleOpen = async (job) => {
        await api.put(`/admin/jobs/${job.id}`, { ...job, open: !job.open });
        load();
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display font-black uppercase text-3xl">Careers</h2>
                    <p className="text-white/50 text-sm mt-1">Job postings shown on <span className="text-brand-primary">/careers</span>. Close a posting to hide it without deleting.</p>
                </div>
                <button
                    onClick={() => setEditing({ ...emptyJob })}
                    className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps transition-colors"
                    data-testid="admin-careers-new"
                >
                    <Plus className="h-4 w-4" /> New Posting
                </button>
            </div>

            <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-careers-list">
                {loading && <div className="p-6 text-white/50">Loading…</div>}
                {!loading && items.length === 0 && (
                    <div className="p-6 text-white/50">No job postings yet. Create the first one — it&apos;ll show up on /careers immediately.</div>
                )}
                {items.map((j) => (
                    <div key={j.id} className="p-4 flex items-center gap-4" data-testid={`admin-career-row-${j.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="font-display font-bold text-white truncate">{j.title || 'Untitled'}</span>
                                <span className={`label-caps text-xs px-2 py-0.5 border ${j.open ? 'text-brand-primary border-brand-primary/40' : 'text-white/40 border-white/20'}`}>
                                    {j.open ? 'Open' : 'Closed'}
                                </span>
                            </div>
                            <div className="text-sm text-white/50">
                                {[j.department, j.location, j.employment_type].filter(Boolean).join(' • ')}
                                {j.salary ? <> • <span className="text-brand-primary">{j.salary}</span></> : null}
                            </div>
                        </div>
                        <button
                            onClick={() => toggleOpen(j)}
                            className="p-2 border border-white/10 hover:bg-white/10 label-caps text-xs"
                            data-testid={`admin-career-toggle-${j.id}`}
                            title={j.open ? 'Close posting' : 'Reopen posting'}
                        >
                            {j.open ? 'Close' : 'Open'}
                        </button>
                        <button
                            onClick={() => setEditing({
                                ...j,
                                requirements: (j.requirements || []).join('\n'),
                                benefits: (j.benefits || []).join('\n'),
                            })}
                            className="p-2 border border-white/10 hover:bg-white/10"
                            data-testid={`admin-career-edit-${j.id}`}
                            aria-label="edit"
                        >
                            <Pencil className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => remove(j.id)}
                            className="p-2 border border-red-500/40 text-red-300 hover:bg-red-500/10"
                            data-testid={`admin-career-delete-${j.id}`}
                            aria-label="delete"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>

            {editing && (
                <Modal
                    title={editing.id ? `Edit — ${editing.title || 'Job Posting'}` : 'New Job Posting'}
                    onClose={() => setEditing(null)}
                    onSave={() => save(editing)}
                    testid="admin-career-modal"
                >
                    <TextField label="Job title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="cf-title" placeholder="e.g. Dry Ice Blasting Technician" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextField label="Department" value={editing.department} onChange={(v) => setEditing({ ...editing, department: v })} testid="cf-department" placeholder="e.g. Operations" />
                        <TextField label="Location" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} testid="cf-location" placeholder="e.g. Raleigh, NC" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <SelectField
                            label="Employment type"
                            value={editing.employment_type}
                            onChange={(v) => setEditing({ ...editing, employment_type: v })}
                            options={[
                                ['Full-time', 'Full-time'],
                                ['Part-time', 'Part-time'],
                                ['Contract', 'Contract'],
                                ['Temporary', 'Temporary'],
                                ['Internship', 'Internship'],
                            ]}
                            testid="cf-employment-type"
                        />
                        <TextField label="Salary / compensation" value={editing.salary} onChange={(v) => setEditing({ ...editing, salary: v })} testid="cf-salary" placeholder="e.g. $22–28/hr + benefits" />
                    </div>
                    <TextArea label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="cf-description" rows={5} />
                    <TextArea label="Requirements (one per line)" value={editing.requirements} onChange={(v) => setEditing({ ...editing, requirements: v })} testid="cf-requirements" rows={5} />
                    <TextArea label="Benefits (one per line)" value={editing.benefits} onChange={(v) => setEditing({ ...editing, benefits: v })} testid="cf-benefits" rows={4} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextField label="Display order (lower = higher on page)" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} testid="cf-order" />
                        <div className="flex items-end">
                            <Check label="Posting is open (visible on /careers)" checked={editing.open} onChange={(v) => setEditing({ ...editing, open: v })} testid="cf-open" />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

const Modal = ({ title, onClose, onSave, children, testid }) => (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
        <div className="w-full max-w-2xl bg-brand-surface border border-white/10 flex flex-col max-h-[calc(100vh-2rem)]">
            <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
                <h3 className="font-display font-black uppercase text-2xl">{title}</h3>
                <button onClick={onClose} className="text-white/60 hover:text-white"><X /></button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">{children}</div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 flex-shrink-0">
                <button onClick={onClose} className="label-caps border border-white/20 hover:bg-white/10 transition-colors px-5 py-3" data-testid={`${testid}-cancel`}>Cancel</button>
                <button onClick={onSave} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-5 py-3 label-caps transition-colors" data-testid={`${testid}-save`}>
                    <Save className="h-4 w-4" /> Save
                </button>
            </div>
        </div>
    </div>
);

const HomeCopyField = ({ label, placeholder, field, form, setForm, hint, multiline }) => {
    const value = (form.home_copy && form.home_copy[field]) || '';
    const onChange = (e) => setForm({ ...form, home_copy: { ...(form.home_copy || {}), [field]: e.target.value } });
    return (
        <div>
            <label className="label-caps text-white/60 block mb-1.5 text-xs">{label}</label>
            {multiline ? (
                <textarea
                    rows={2}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    className="w-full bg-brand-surface2 border border-white/10 focus:border-brand-primary transition-colors text-white p-3 outline-none text-sm resize-y"
                    data-testid={`home-copy-${field}`}
                />
            ) : (
                <input
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    className="w-full bg-brand-surface2 border border-white/10 focus:border-brand-primary transition-colors text-white p-3 outline-none text-sm"
                    data-testid={`home-copy-${field}`}
                />
            )}
            {hint ? <div className="mt-1 text-white/40 text-[11px]">{hint}</div> : null}
        </div>
    );
};


export default AdminDashboard;
